import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  TextInput, 
  //DatePicker,
  //DatePickerInput,
  TextArea,
  RadioButtonGroup,
  RadioButton,
  Checkbox,
  
} from "@carbon/react";

import CommonFields from "../../common/CommonField";

// Required label helper - removed star
const requiredLabel = (text) => (
  <>
    {text} <span style={{ color: "#da1e28" }}>*</span>
  </>
);

const RentalDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 3,
  lastSavedStep = 0,
  isEditMode = false,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  // DEBUG: 
  console.log('🔍 RentalDetails (Step 3) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditMode,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0
  });

  //  EDIT MODE CALCULATION
  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  const FIELD_MAP = useMemo(() => ({
    propertyAvailableFor: "rent_property_available_for",
    expectedRent: "rent_expected_rent",
    expectedDeposit: "rent_expected_deposit",
    expectedLeaseAmount: "rent_expected_lease_amount",
    priceNegotiable: "rent_negotiable",
    monthlyMaintenance: "rent_monthly_maintenance",
    maintenanceAmount: "rent_maintenance_amount",
    availableFrom: "rent_available_from",
    preferredTenants: "rent_preferred_tenants",
    furnishing: "rent_furnishing",
    parking: "rent_parking",
    description: "rent_description",
  }), []);

  const [form, setForm] = useState({
    propertyAvailableFor: "",
    expectedRent: "",
    expectedDeposit: "",
    expectedLeaseAmount: "",
    priceNegotiable: false,
    monthlyMaintenance: "",
    maintenanceAmount: "",
    availableFrom: "",
    preferredTenants: [],
    furnishing: "",
    parking: "",
    description: "",
  });

  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

    // Parse "dd/mm/yyyy" (or ISO) -> Date object for Carbon's DatePicker
  const parseDateFromString = useCallback((str) => {
    if (!str) return null;
    if (str instanceof Date && !isNaN(str.getTime())) return str;

    const s = String(str).trim();

    // dd/mm/yyyy  or  d/m/yyyy
    const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dmy) {
      const [, d, m, y] = dmy;
      const dt = new Date(Number(y), Number(m) - 1, Number(d));
      return isNaN(dt.getTime()) ? null : dt;
    }

    // ISO or anything else the browser can parse
    const dt = new Date(s);
    return isNaN(dt.getTime()) ? null : dt;
  }, []);

  // API OPTIONS ONLY
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP] 
  );

  const getOptionLabel = useCallback((fieldKey, rawValue) => {
    if (!rawValue) return "";
    const opts = getOptions(fieldKey);
    const found = opts.find((o) => String(o.id) === String(rawValue));
    return found ? found.label : rawValue;
  }, [getOptions]);

  const isRent = useCallback(() => {
    return getOptionLabel("propertyAvailableFor", form.propertyAvailableFor)
      .toLowerCase()
      .includes("rent");
  }, [getOptionLabel, form.propertyAvailableFor]);

  const isLease = useCallback(() => {
    return getOptionLabel("propertyAvailableFor", form.propertyAvailableFor)
      .toLowerCase()
      .includes("lease");
  }, [getOptionLabel, form.propertyAvailableFor]);

  // Check if maintenance is extra (needs amount)
  const isMaintenanceExtra = useCallback(() => {
    const maintenanceLabel = getOptionLabel("monthlyMaintenance", form.monthlyMaintenance);
    return maintenanceLabel.toLowerCase().includes("extra");
  }, [getOptionLabel, form.monthlyMaintenance]);

  // LOAD INITIAL DATA 
  useEffect(() => {
    if (!data?.attributes) return;

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = data.attributes[apiKey];
      if (raw == null) return;

      if (field === "preferredTenants") {
        incoming[field] = Array.isArray(raw)
          ? raw.map((x) => String(x)) // store as strings for checkbox
          : [];
      } else if (field === "priceNegotiable") {
        incoming[field] = raw === 1 || raw === true;
      } else {
        incoming[field] = raw;
      }
    });

    console.log('💧 RentalDetails hydrating form with data:', incoming);
    setForm((prev) => ({ ...prev, ...incoming }));
  }, [data, FIELD_MAP]); 

  // DATE
  const handleDateChange = useCallback((dates) => {
    if (!dates?.[0]) return;

    const d = dates[0];
    const formatted = `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;

    updateField("availableFrom", formatted);
  }, [updateField]);

  // SIMPLIFIED VALIDATION - Just "Please fill required field"
  const getFieldError = useCallback((field) => {
    const v = form[field];

    // Required fields check
    if (field === "propertyAvailableFor" && !v) {
      return "Property available is required";
    }

    if (field === "preferredTenants" && (!v || v.length === 0)) {
      return "Preferred tenants is required ";
    }

    // Removed validation for availableFrom

    if (field === "furnishing" && !v) {
      return "Furnishing is required";
    }

    if (field === "parking" && !v) {
      return "Parking is required";
    }

    // FIXED: Monthly Maintenance is required
    if (field === "monthlyMaintenance" && !v) {
      return "Monthly maintanance is required";
    }

    // Conditional validation based on property type
    if (isRent()) {
      if (field === "expectedRent" && (!v || Number(v) <= 0)) {
        return "Expected rent is required";
      }
      if (field === "expectedDeposit" && (!v || Number(v) <= 0)) {
        return "Expected deposit is required";
      }
    }

    if (isLease() && field === "expectedLeaseAmount" && (!v || Number(v) <= 0)) {
      return "Expected lease amount is required";
    }

    // Maintenance amount validation when extra is selected
    if (field === "maintenanceAmount" && isMaintenanceExtra()) {
      if (!v || v === "" || Number(v) <= 0) {
        return "Maintenance amount is required";
      }
    }

    return null;
  }, [form, isRent, isLease, isMaintenanceExtra]);

  // Form validation for save - Returns true if any required field is missing
  const hasRequiredFieldsMissing = useCallback(() => {
    // Check all required fields (availableFrom removed from required check)
    if (!form.propertyAvailableFor) return true;
    if (!form.preferredTenants || form.preferredTenants.length === 0) return true;
    // Removed availableFrom check
    if (!form.furnishing) return true;
    if (!form.parking) return true;
    if (!form.monthlyMaintenance) return true; // FIXED: Monthly maintenance is required
    
    if (isRent()) {
      if (!form.expectedRent || Number(form.expectedRent) <= 0) return true;
      if (!form.expectedDeposit || Number(form.expectedDeposit) <= 0) return true;
    }
    
    if (isLease() && (!form.expectedLeaseAmount || Number(form.expectedLeaseAmount) <= 0)) return true;
    
    if (isMaintenanceExtra()) {
      if (!form.maintenanceAmount || form.maintenanceAmount === "" || Number(form.maintenanceAmount) <= 0) return true;
    }
    
    return false;
  }, [form, isRent, isLease, isMaintenanceExtra]);

  // Mark all fields as touched for validation
  const markAllTouched = useCallback(() => {
    const allTouched = {};
    Object.keys(form).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(prev => ({ ...prev, ...allTouched }));
  }, [form]);

  // Dropdown renderer
  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  // SAVE 
  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 RentalDetails handleSave:', { navigateNext, isEditForm, currentStep });
    
    // Mark all fields as touched to show validation
    markAllTouched();

    // Check if any required fields are missing - FIXED: Show simple message
    if (hasRequiredFieldsMissing()) {
      setError("Please fill required field");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      // Create property if it doesn't exist (should already exist from Step 1)
      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 3...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        // Skip maintenanceAmount if maintenance is not extra
        if (field === "maintenanceAmount" && !isMaintenanceExtra()) continue;

        if (field === "preferredTenants") {
          payload[apiKey] = form.preferredTenants.map((id) => Number(id)); 
        } else if (field === "priceNegotiable") {
          payload[apiKey] = v ? 1 : 0;
        } else if (field === "maintenanceAmount" || field === "expectedRent" || 
                  field === "expectedDeposit" || field === "expectedLeaseAmount") {
          // Ensure numeric fields are sent as numbers
          payload[apiKey] = Number(v);
        } else {
          payload[apiKey] = v;
        }
      }

      console.log('💾 Saving rental details:', payload);
      await saveSectionAttributes(uuid, payload, form, "rental_details");

      setSuccess("Rental details saved successfully!");
      
      // Call parent's onSaveSuccess with step number
      onSaveSuccess?.(uuid, form, currentStep);

      // Navigate to next step if requested
      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save rental details");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    hasRequiredFieldsMissing,
    markAllTouched,
    isEditForm,
    currentStep,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onNavigateNext,
    isMaintenanceExtra
  ]);
 

  // UI
  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column sm={4} md={8}  lg={16}>
          <Tile style={{  backgroundColor:"white"}} >
    <div style={{ textAlign: "center", marginBottom: "0.75rem" }}>
  <p
    style={{
      color: "#0f62fe",
      fontSize: "1rem",
      fontWeight: 500,
      letterSpacing: "0.2px",
      maxWidth: "600px",
      margin: "0 auto",
      lineHeight: "1.5",
    }}
  >
Provide rental details about your property   </p>
</div>

            <Stack gap={6}>
              
              {/* Property Available For */}
              <FormGroup legendText={requiredLabel("Property available for")}>
                <RadioButtonGroup
                  name="propertyAvailableFor"
                  valueSelected={form.propertyAvailableFor}
                  onChange={(v) => updateField("propertyAvailableFor", v)}
                  onBlur={() => handleBlur("propertyAvailableFor")}
                >
                  {getOptions("propertyAvailableFor").map((o) => (
                    <RadioButton key={o.id} value={o.id} labelText={o.label} />
                  ))}
                </RadioButtonGroup>
                {touched.propertyAvailableFor && getFieldError("propertyAvailableFor") && (
                  <div style={{ color: "#da1e28", marginTop: 6, fontSize: "0.75rem" }}>
                    {getFieldError("propertyAvailableFor")}
                  </div>
                )}
              </FormGroup>

              {/* RENT */}
              {isRent() && (
                <FormGroup>
                  <Grid>
                    <Column md={4} sm={4} lg={8}>
                     {renderNumberField(
                      "expectedRent", "Expected Rent", "Enter amount", true
                     )}
                    </Column>
                    <Column sm={4} md={4} lg={8}>
                     {renderNumberField(
                      "expectedDeposit", "Expected Deposit", "Enter amount", true
                     )}
                    </Column>
                  </Grid>
                </FormGroup>
              )}

              {/* LEASE */}
              {isLease() && (
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderNumberField(
                        "expectedLeaseAmount", "Expected Lease Amount", "Enter amount", true
                      )}
                    </Column>
                  </Grid>
                </FormGroup>
              )}
              
              <FormGroup>
                <Column lg={8}>
                  <Checkbox
                    id="priceNegotiable"
                    labelText="Price Negotiable"
                    checked={form.priceNegotiable}
                    onChange={(e) =>
                      updateField("priceNegotiable", e.target.checked)
                    }
                    onBlur={() => handleBlur("priceNegotiable")}
                  />
                </Column>
              </FormGroup>

              <FormGroup>
                <Grid>
                  <Column sm={4} md={4}  lg={8}>
                    {renderDropdown(
                      "monthlyMaintenance",
                      "Monthly Maintenance",
                      true 
                    )}
                  </Column>

                  <Column sm={4} md={4}  lg={8}>
                    {isMaintenanceExtra() && renderNumberField(
                        "maintenanceAmount", 
                        "Maintenance Amount", 
                        "Enter amount", 
                        true
                      )
                    }
                  </Column>
                </Grid>
              </FormGroup>
                    
              {/* AVAILABLE FROM - Removed required indicator and validation */}

<FormGroup>
  <Grid>
    <Column sm={4} md={4} lg={8}>
      <TextInput
        id="availableFrom"
        type="date"
        labelText="Available From"
        value={form.availableFrom}
        onChange={(e) => updateField("availableFrom", e.target.value)}
        onBlur={() => handleBlur("availableFrom")}
      />
    </Column>
  </Grid>
</FormGroup>

              {/* PREFERRED TENANTS */}
              <FormGroup legendText={requiredLabel("Preferred Tenants")}>
                <Grid>
                  {getOptions("preferredTenants").map((o) => (
                    <Column sm={4} md={4} lg={8} key={o.id}>
                      <Checkbox
                        id={`tenant-${o.id}`}
                        labelText={o.label}
                        checked={form.preferredTenants.includes(String(o.id))}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...form.preferredTenants, String(o.id)]
                            : form.preferredTenants.filter(
                                (x) => x !== String(o.id)
                              );
                          updateField("preferredTenants", next);
                        }}
                        onBlur={() => handleBlur("preferredTenants")}
                      />
                    </Column>
                  ))}
                </Grid>

                {touched.preferredTenants &&
                  getFieldError("preferredTenants") && (
                    <div style={{ color: "#da1e28", marginTop: 6, fontSize: "0.75rem" }}>
                      {getFieldError("preferredTenants")}
                    </div>
                  )}
              </FormGroup>

              {/* FURNISHING / PARKING */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4}  lg={8}>
                    {renderDropdown("furnishing","Furnishing", true)}
                  </Column>
                  <Column sm={4} md={4}  lg={8}>
                    {renderDropdown("parking", "Parking", true)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* DESCRIPTION */}
              <FormGroup>
                <TextArea
                  id="description"
                  labelText="Description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  onBlur={() => handleBlur("description")}
                  placeholder="Write a few lines about your property something which is special and makes your
                               property stand out. Please do not mention your contact details in any format."   
                />
              </FormGroup>

              {/* BUTTONS */}
              {CommonButtons && (
                  <CommonButtons
                    onSave={() => handleSave(false)}   // SAVE ONLY
                    onNext={() => handleSave(true)}     // NEXT = SAVE + NAVIGATE
                    showSave
                    showNext
                    isSaveLoading={saving}
                  />
                )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default RentalDetails;